import React, { MouseEvent, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { getBlockConfig } from "../../models/block";
import { colors } from "../../styles/theme";
import { BlockImage } from "../block/BlockImage";
//import { useAppSelector } from "../../hooks";
//import { selectAccountBalance } from "../../store/game/selectors";
import { BlockTypes } from "../../models/block-types";

interface Props {
  blockType: BlockTypes;
  onItemClick: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    blockType: BlockTypes,
  ) => void;
  disableInteractivity?: boolean;
  onItemDrop?: (clientX: number, clientY: number, blockType: BlockTypes) => void;
}

export const PaletteItem: React.FC<Props> = ({
  blockType,
  onItemClick,
  disableInteractivity = false,
  onItemDrop,
}) => {
  const config = getBlockConfig(blockType);
  // const accountBalance = useAppSelector((state) =>
  //   selectAccountBalance(state.game),
  // );
  //const canAfford = accountBalance >= config.purchasePrice;
  const isDisabled = disableInteractivity; // && !canAfford;

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchIdRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const cloneRef = useRef<HTMLDivElement | null>(null);

  function handleTouchMove(e: TouchEvent) {
    if (touchIdRef.current === null || touchStartRef.current === null) return;

    let touch: Touch | null = null;
    for (let i = 0; i < e.touches.length; i++) {
      if (e.touches[i].identifier === touchIdRef.current) {
        touch = e.touches[i];
        break;
      }
    }
    if (!touch) return;

    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const distance = Math.hypot(dx, dy);

    if (!isDraggingRef.current && distance > 10) {
      isDraggingRef.current = true;

      // Create a floating clone
      const clone = document.createElement("div");
      clone.style.position = "fixed";
      clone.style.width = "60px";
      clone.style.height = "60px";
      clone.style.pointerEvents = "none";
      clone.style.zIndex = "9999";
      clone.style.opacity = "0.7";
      clone.style.border = "solid black 1px";
      clone.style.borderRadius = "5px";
      clone.style.backgroundColor = "white";
      clone.style.display = "flex";
      clone.style.justifyContent = "center";
      clone.style.alignItems = "center";

      // Find the image element within the palette item and clone it
      const element = document.querySelector(`[data-testid="palette-item-${blockType}"]`);
      const img = element?.querySelector("img");
      if (img) {
        const imgClone = img.cloneNode(true) as HTMLElement;
        imgClone.style.width = "50px";
        imgClone.style.height = "50px";
        clone.appendChild(imgClone);
      }

      document.body.appendChild(clone);
      cloneRef.current = clone;
    }

    if (isDraggingRef.current && cloneRef.current) {
      cloneRef.current.style.left = `${touch.clientX - 30}px`;
      cloneRef.current.style.top = `${touch.clientY - 30}px`;

      if (e.cancelable) {
        e.preventDefault();
      }
    }
  }

  function handleTouchEnd(e: TouchEvent) {
    if (touchIdRef.current === null) return;

    let touch: Touch | null = null;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === touchIdRef.current) {
        touch = e.changedTouches[i];
        break;
      }
    }

    if (isDraggingRef.current && touch) {
      const clientX = touch.clientX;
      const clientY = touch.clientY;

      const paletteContainer = document.getElementById("palette-container");
      let droppedOnPalette = false;
      if (paletteContainer) {
        const rect = paletteContainer.getBoundingClientRect();
        if (
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          droppedOnPalette = true;
        }
      }

      if (!droppedOnPalette && onItemDrop) {
        onItemDrop(clientX, clientY, blockType);
      }
    }

    if (cloneRef.current) {
      if (document.body.contains(cloneRef.current)) {
        document.body.removeChild(cloneRef.current);
      }
      cloneRef.current = null;
    }
    isDraggingRef.current = false;
    touchIdRef.current = null;
    touchStartRef.current = null;

    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
    window.removeEventListener("touchcancel", handleTouchCancel);
  }

  function handleTouchCancel() {
    if (cloneRef.current) {
      if (document.body.contains(cloneRef.current)) {
        document.body.removeChild(cloneRef.current);
      }
      cloneRef.current = null;
    }
    isDraggingRef.current = false;
    touchIdRef.current = null;
    touchStartRef.current = null;

    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
    window.removeEventListener("touchcancel", handleTouchCancel);
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (disableInteractivity || !onItemDrop) return;
    const touch = e.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchIdRef.current = touch.identifier;
    isDraggingRef.current = false;
    cloneRef.current = null;

    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: false });
    window.addEventListener("touchcancel", handleTouchCancel, { passive: false });
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchCancel);
      if (cloneRef.current && document.body.contains(cloneRef.current)) {
        document.body.removeChild(cloneRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backgroundColor = isDisabled ? colors.grey[300] : "white";
  const hoverBackgroundColor = isDisabled ? colors.grey[300] : colors.grey[100];
  const cursor = isDisabled ? "not-allowed" : "pointer";
  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/reactflow", blockType);
        e.dataTransfer.effectAllowed = "move";

        const img = e.currentTarget.querySelector("img");
        if (img) {
          const clone = img.cloneNode(true) as HTMLElement;
          clone.style.opacity = "0.5";
          clone.style.position = "absolute";
          clone.style.top = "-1000px";
          clone.style.left = "-1000px";
          document.body.appendChild(clone);
          e.dataTransfer.setDragImage(clone, 0, 0);

          // Remove the clone after a short delay to ensure it's used for the drag image
          setTimeout(() => {
            document.body.removeChild(clone);
          }, 0);
        }
      }}
      onTouchStart={handleTouchStart}
      sx={{
        width: "100%",
        cursor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        borderRadius: "5px",
        padding: "4px 0",
        backgroundColor,
        border: "solid black 1px",
        "&:hover": {
          backgroundColor: hoverBackgroundColor,
        },
        minHeight: "85px",
      }}
      data-testid={`palette-item-${blockType}`}
      onClick={(e) => onItemClick(e, blockType)}
    >
      <Box
        sx={{
          width: "60px",
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BlockImage width={50} height={50} blockType={blockType} />
      </Box>
      {!disableInteractivity && (
        <Box
          sx={{
            fontSize: "9px",
            fontWeight: 600,
            textAlign: "center",
            lineHeight: 1.2,
            color: "#333",
            px: "2px",
            wordBreak: "break-word",
          }}
        >
          {config.displayName}
          {/* TODO: Add account price back in someday:  : $${config.purchasePrice}*/}
        </Box>
      )}
    </Box>
  );
};
